import type { UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { petConditionValues } from "./lib";

const petProfileSchema = z.object({
  name: z.string().min(1, "Name is required"),
  breed: z.string().min(1, "Breed is required"),
  age: z
    .string()
    .min(1, "Age is required")
    .refine((val) => /^\d.*[a-zA-Z]$/.test(val), {
      message: "Age must be a number and include a unit (e.g., 2 yrs)",
    }),
  gender: z.enum(["male", "female"], "Gender is required"),
  type: z.enum(["dog", "cat"], "Type is invalid").optional().default("cat"),
  special_conditions: z
    .array(
      z.object({
        value: z.string(),
        selected: z.boolean(),
      }),
    )
    .default([]),
});

export type PetProfileFormData = z.input<typeof petProfileSchema>;

export interface PetProfileFormReturn
  extends UseFormReturn<PetProfileFormData> {
  defaultValues: PetProfileFormData;
}

export const initialFormValues: PetProfileFormData = {
  name: "",
  breed: "",
  age: "",
  gender: "male",
  type: "cat",
  special_conditions: [...petConditionValues],
};

export const usePetProfileForm = (
  initialValues?: PetProfileFormData,
): PetProfileFormReturn => {
  const defaultValues = initialValues || initialFormValues;

  const form = useForm<PetProfileFormData>({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(petProfileSchema),
  });

  return {
    ...form,
    defaultValues,
  };
};
